/** New pre-trip ITEM dialog: pick a tenant, a category (scoped to that tenant),
 *  a name, and the display mode (link / file / text). The mode's active field is
 *  captured and the inactive fields are nulled on submit — link → url+deep_link,
 *  file → multipart attachment File, text → HTML. The text mode is hidden when
 *  the selected category's `rendering_type === 2` (List with checkboxes), same
 *  as the detail editor. Multipart POST when a File is staged (file mode); plain
 *  JSON otherwise. Navigates to the created item. */
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as itemsApi from '@/api/preTripItems';
import type { DisplayMode, PreTripItemWriteJson } from '@/api/preTripItems';
import * as typesApi from '@/api/preTripItemTypes';
import type { PreTripItemTypeRow } from '@/api/preTripItemTypes';
import { AttachmentUploadField, type AttachmentFieldValue } from '@/components/AttachmentUploadField/AttachmentUploadField';
import { QuillEditor } from '@/components/RichText/QuillEditor';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

export function PreTripItemCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [category, setCategory] = useState<PreTripItemTypeRow | null>(null);
  const [name, setName] = useState('');
  const [mode, setMode] = useState<DisplayMode>('link');
  const [url, setUrl] = useState('');
  const [deepLink, setDeepLink] = useState(false);
  const [attachment, setAttachment] = useState<AttachmentFieldValue>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Text mode is not available for "List with checkboxes" categories.
  const textAllowed = category?.rendering_type !== 2;
  const effectiveMode: DisplayMode = mode === 'text' && !textAllowed ? 'link' : mode;

  const reset = () => {
    resetTenant();
    setCategory(null);
    setName('');
    setMode('link');
    setUrl('');
    setDeepLink(false);
    setAttachment(null);
    setText('');
    setError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => {
      // Resolve the mode: send the active field, null the inactive.
      const body: PreTripItemWriteJson = {
        tour_operator: tenant!.id,
        item_type: category!.id,
        name: name.trim(),
      };
      let file: File | null = null;
      if (effectiveMode === 'link') {
        body.url = url.trim() || null;
        body.deep_link = deepLink;
        body.text = null;
        body.attachment = null;
      } else if (effectiveMode === 'text') {
        body.text = text || null;
        body.url = null;
        body.attachment = null;
      } else {
        // file
        body.url = null;
        body.text = null;
        if (attachment instanceof File) file = attachment;
        else body.attachment = null;
      }
      return itemsApi.create(body, file);
    },
    onSuccess: (item) => {
      snackbar.show('Item created.', 'success');
      qc.invalidateQueries({ queryKey: ['pre_trip_items'] });
      reset();
      onClose();
      navigate(`/pre-trip-items/${item.id}`);
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError) {
        const body = e.body as Record<string, unknown> | undefined;
        const nameErr = body?.name;
        if (Array.isArray(nameErr) && typeof nameErr[0] === 'string') {
          setError(nameErr[0]);
          return;
        }
        setError(e.message);
        return;
      }
      setError('Create failed.');
    },
  });

  const canSubmit =
    tenant !== null && category !== null && name.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New item</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TenantPicker
            value={tenant}
            featured={pageTenants}
            onChange={(t) => { setTenant(t); setCategory(null); }}
            label="Operator" placeholder="Select operator"
          />
          <RemoteAutocomplete<PreTripItemTypeRow>
            label="Category" size="small" value={category}
            getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
            fetchOnEmpty={tenant !== null}
            queryFn={async (q) =>
              tenant
                ? (await typesApi.list({ tour_operator_id: tenant.id, search: q || undefined, pageSize: 50 })).results
                : []
            }
            onChange={(v) => setCategory((v as PreTripItemTypeRow | null) ?? null)}
          />
          <TextField
            label="Name" size="small" fullWidth value={name} required
            onChange={(e) => setName(e.target.value)}
          />

          <RadioGroup row value={effectiveMode} onChange={(e) => setMode(e.target.value as DisplayMode)}>
            <FormControlLabel value="link" control={<Radio size="small" />} label="Link" />
            <FormControlLabel value="file" control={<Radio size="small" />} label="File" />
            {textAllowed && (
              <FormControlLabel value="text" control={<Radio size="small" />} label="Text" />
            )}
          </RadioGroup>

          {effectiveMode === 'link' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                label="URL" size="small" fullWidth value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              <FormControlLabel
                control={<Checkbox checked={deepLink} onChange={(e) => setDeepLink(e.target.checked)} size="small" />}
                label="Open in Safari (instead of the in-app browser)"
              />
            </Box>
          )}
          {effectiveMode === 'file' && (
            <AttachmentUploadField
              label="Attachment" currentUrl={null}
              value={attachment} onChange={setAttachment} canManage
            />
          )}
          {effectiveMode === 'text' && (
            <QuillEditor label="Text" value={text} onChange={setText} />
          )}

          {error && <Box sx={{ color: 'error.main', fontSize: 13 }}>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Creating…' : 'Create item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
